import ReturnRequestPdfFormPdfMake from './pdf-forms/ReturnRequestPdfFormPdfMake';
import RepairOpeningPdfFormPdfMake from './pdf-forms/RepairOpeningPdfFormPdfMake';
import RepairClosingPdfFormPdfMake from './pdf-forms/RepairClosingPdfFormPdfMake';
import ClaimCardPdfFormPdfMake from './pdf-forms/ClaimCardPdfFormPdfMake';
import OtherCostClaimCardPdfFormPdfMake from './pdf-forms/OtherCostClaimCardPdfFormPdfMake';
import WarrantyAuditPdfFormPdfMake from './pdf-forms/WarrantyAuditPdfFormPdfMake';

/**
 * PDF Oluşturma Ana Servisi
 *
 * Bu servis, farklı PDF formlarını oluşturmak için factory pattern kullanır.
 * Her form tipi kendi sınıfında tanımlanmıştır ve bu servis üzerinden erişilir.
 *
 * Tüm formlar pdfMake kütüphanesi kullanılarak oluşturulur - Türkçe karakter desteği tam.
 *
 * Kullanılabilir Formlar:
 * - ReturnRequestPdfFormPdfMake: Arızalı Yedek Parça İade Dökümü
 * - RepairOpeningPdfFormPdfMake: Onarım Açılış Formu
 * - RepairClosingPdfFormPdfMake: Onarım Kapanış Formu
 */
class PdfGenerationService {
    constructor() {
        // Form instance'larını lazy loading ile oluştur
        this._returnRequestForm = null;
        this._repairOpeningForm = null;
        this._repairClosingForm = null;
        this._claimCardForm = null;
        this._otherCostClaimCardForm = null;
        this._warrantyAuditForm = null;
    }

    /**
     * ReturnRequestPdfForm instance'ını döndürür (lazy loading)
     * pdfMake kullanır - Türkçe karakter desteği için
     */
    get returnRequestForm() {
        if (!this._returnRequestForm) {
            this._returnRequestForm = new ReturnRequestPdfFormPdfMake();
        }
        return this._returnRequestForm;
    }

    /**
     * RepairOpeningPdfForm instance'ını döndürür (lazy loading)
     * pdfMake kullanır - Türkçe karakter desteği için
     */
    get repairOpeningForm() {
        if (!this._repairOpeningForm) {
            this._repairOpeningForm = new RepairOpeningPdfFormPdfMake();
        }
        return this._repairOpeningForm;
    }

    /**
     * RepairClosingPdfForm instance'ını döndürür (lazy loading)
     * pdfMake kullanır - Türkçe karakter desteği için
     */
    get repairClosingForm() {
        if (!this._repairClosingForm) {
            this._repairClosingForm = new RepairClosingPdfFormPdfMake();
        }
        return this._repairClosingForm;
    }

    /**
     * ClaimCardPdfForm instance'ını döndürür (lazy loading)
     * pdfMake kullanır - Türkçe karakter desteği için
     */
    get claimCardForm() {
        if (!this._claimCardForm) {
            this._claimCardForm = new ClaimCardPdfFormPdfMake();
        }
        return this._claimCardForm;
    }

    /**
     * OtherCostClaimCardPdfForm instance'ını döndürür (lazy loading)
     */
    get otherCostClaimCardForm() {
        if (!this._otherCostClaimCardForm) {
            this._otherCostClaimCardForm = new OtherCostClaimCardPdfFormPdfMake();
        }
        return this._otherCostClaimCardForm;
    }

    /**
     * Arızalı Yedek Parça İade Dökümü PDF'i oluşturur
     * @param {Object} data - PDF için gerekli veriler
     * @param {Object} translations - Dil çevirileri
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateReturnRequestPdf(data, translations = {}) {
        return this.returnRequestForm.generate(data, translations);
    }

    /**
     * Onarım Açılış Formu PDF'i oluşturur
     * @param {Object} data - PDF için gerekli veriler
     * @param {Object} translations - Dil çevirileri
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateRepairOpeningFormPdf(data, translations = {}) {
        return this.repairOpeningForm.generate(data, translations);
    }

    /**
     * Onarım Kapanış Formu PDF'i oluşturur
     * @param {Object} data - PDF için gerekli veriler
     * @param {Object} translations - Dil çevirileri
     * @param {Object} options - { halfPage: boolean } yarım A4 (A5) çıktı seçeneği
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateRepairClosingFormPdf(data, translations = {}, options = {}) {
        return this.repairClosingForm.generate(data, translations, options);
    }

    /**
     * Garantiden Değişen Parça Takip Fişi PDF'i oluşturur
     * @param {Object} data - PDF için gerekli veriler
     * @param {Object} translations - Dil çevirileri
     * @param {Object} options - { halfPage: boolean } yarım A4 (A5) çıktı seçeneği
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateClaimCardPdf(data, translations = {}, options = {}) {
        return this.claimCardForm.generate(data, translations, options);
    }

    /**
     * Diğer Maliyet kalemi için Garanti Takip Fişi PDF'i oluşturur
     * v1/warrantyclaimsothercost/getitempdf response yapısını kullanır, parça tablosu içermez.
     * @param {Object} data - PDF için gerekli veriler
     * @param {Object} translations - Dil çevirileri
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateOtherCostClaimCardPdf(data, translations = {}) {
        return this.otherCostClaimCardForm.generate(data, translations);
    }

    /**
     * WarrantyAuditPdfForm instance'ını döndürür (lazy loading)
     */
    get warrantyAuditForm() {
        if (!this._warrantyAuditForm) {
            this._warrantyAuditForm = new WarrantyAuditPdfFormPdfMake();
        }
        return this._warrantyAuditForm;
    }

    /**
     * Garanti Denetim Formu PDF'i oluşturur
     * @param {Object} data - WarrantyAuditPdfDataDto
     * @param {Object} translations - Dil çevirileri
     * @returns {Object} - pdfMake PDF dökümanı (open/download metodları ile kullan)
     */
    generateWarrantyAuditPdf(data, translations = {}) {
        return this.warrantyAuditForm.generate(data, translations);
    }

    /**
     * PDF'i görüntüler (yeni sekmede açar)
     * Hem jsPDF hem pdfMake dökümanlarını destekler
     */
    viewPdf(pdfDoc) {
        // pdfMake dökümanı mı kontrol et
        if (pdfDoc && typeof pdfDoc.open === 'function') {
            pdfDoc.open();
        } else {
            // jsPDF dökümanı
            const pdfBlob = pdfDoc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');
        }
    }

    /**
     * PDF'i indirir
     * Hem jsPDF hem pdfMake dökümanlarını destekler
     */
    downloadPdf(pdfDoc, filename = 'dokuman.pdf') {
        // pdfMake dökümanı mı kontrol et
        if (pdfDoc && typeof pdfDoc.download === 'function') {
            pdfDoc.download(filename);
        } else {
            // jsPDF dökümanı
            pdfDoc.save(filename);
        }
    }
}

export default PdfGenerationService;
